import {Stack} from '@mantine/core';
import React from 'react';

import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {SettingsLabel} from 'ui-library/SettingsField';

import {
  isMoleculeSchemaInterfaceId,
  MoleculeSchemaInterfaceApiId,
  MOLECULE_SCHEMA_INTERFACE_DISPLAY_NAMES,
} from './MoleculeSchemaInterfaceConstants';

export type MoleculeSchemaInterfaceOption = MoleculeSchemaInterfaceApiId | null;

const NONE_DISPLAY_NAME = 'None';

const OPTIONS: Array<OptionData> = [
  {name: NONE_DISPLAY_NAME, value: null},
  ...Object.values(MoleculeSchemaInterfaceApiId).map((id) => ({
    name: MOLECULE_SCHEMA_INTERFACE_DISPLAY_NAMES[id],
    value: id,
  })),
];

export interface MoleculeSchemaInterfaceConfigurationProps {
  disabled: boolean;
  onChange: (value: MoleculeSchemaInterfaceOption) => void;
  value: MoleculeSchemaInterfaceOption;
}

export function getMoleculeSchemaInterfaceValue(
  implementedInterfaces: ReadonlyArray<ImplementedStaticSchemaInterface>
): MoleculeSchemaInterfaceOption {
  const match = implementedInterfaces.find((impl) => isMoleculeSchemaInterfaceId(impl.interfaceId));
  return (match?.interfaceId as MoleculeSchemaInterfaceOption) ?? null;
}

/**
 * Dropdown component for configuring molecule schema interfaces (Molecule Compound or Molecule Form).
 * Only relevant for entity schemas of type Molecule. Gated by CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE.
 */
export function MoleculeSchemaInterfaceConfiguration({
  disabled,
  onChange,
  value,
}: MoleculeSchemaInterfaceConfigurationProps): JSX.Element {
  const displayName = value != null ? MOLECULE_SCHEMA_INTERFACE_DISPLAY_NAMES[value] : NONE_DISPLAY_NAME;
  return (
    <Stack spacing="xs" w="50%">
      <SettingsLabel label="Molecule component type" />
      <SelectDropdown
        menu={
          <SearchableMenu
            optionDatas={OPTIONS}
            onSelect={onChange}
            selectedValue={value}
            width={Menu.WIDTHS.MEDIUM}
          />
        }
        displayName={displayName}
        disabled={disabled}
        alwaysDropDown={true}
        dataTestAttrs={{
          component: 'MoleculeSchemaInterfaceConfiguration',
          element: 'dropdown',
        }}
      />
    </Stack>
  );
}
